import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setPlayers, setGameID } from '../../store/gameSlice';
import { authHeader } from '../../services/authHeader';
import styles from './StartGame.module.css';

const StartGame = () => {
    const username = useSelector((state) => state.user.username);
    const [team1Player1, setTeam1Player1] = useState('');
    const [team1Player2, setTeam1Player2] = useState('');
    const [team2Player1, setTeam2Player1] = useState('');
    const [team2Player2, setTeam2Player2] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [otherPlayers, setOtherPlayers] = useState([]);
    const [selectedPlayer, setSelectedPlayer] = useState(null);
    const [errorMessage, setErrorMessage] = useState(null);
    const [searchedPlayers, setSearchedPlayers] = useState([]);
    const [slamSlot, setSlamSlot] = useState(null);
    const [flashing, setFlashing] = useState(false);

    const dispatch = useDispatch();
    const navigate = useNavigate();

    useEffect(() => {
        if (username) {
            setTeam1Player1(username);
        }
    }, [username]);

    const triggerSlam = (slotKey) => {
        setSlamSlot(slotKey);
        setTimeout(() => setSlamSlot(null), 450);
    };

    const handleSearchUsername = async () => {
        if (!searchQuery.trim()) {
            setErrorMessage('Please enter a username to search.');
            return;
        }

        if (username && searchQuery.trim().toLowerCase() === username.toLowerCase()) {
            setErrorMessage("That's you! You're already in the game.");
            return;
        }

        setErrorMessage(null);
        setSelectedPlayer(null);

        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/c/searchusername/`, { params: { username: searchQuery, player: username, searched: searchedPlayers }, ...authHeader() });
            if (response.data.suggestions) {
                setOtherPlayers(response.data.suggestions);
            } else {
                setErrorMessage('No players found.');
            }
        } catch (err) {
            console.error('Error fetching nickname:', err);

            if (err.response && err.response.data && err.response.data.error) {
                setErrorMessage(err.response.data.error);
            } else {
                setErrorMessage('No players with that username were found.');
            }
        }
    };

    const handleSelectPlayer = (player) => {
        const playerUsername = player.username;

        if ([team1Player1, team1Player2, team2Player1, team2Player2].includes(playerUsername)) {
            setErrorMessage("Player is already assigned to a team.");
            return;
        }

        setSelectedPlayer(player);
        setErrorMessage(null);
    };

    const handleAssignOtherPlayer = (player, team, position) => {
        const playerUsername = player.username;

        if ([team1Player1, team1Player2, team2Player1, team2Player2].includes(playerUsername)) {
            setErrorMessage("Player already assigned to a team.");
            return;
        }

        if (searchedPlayers.includes(playerUsername)) {
            setErrorMessage("Player already selected.");
            return;
        }

        setSearchedPlayers(prevSearchedPlayers => [...prevSearchedPlayers, playerUsername]);

        if (team === 'home') {
            if (position === 1) {
                setTeam1Player1(playerUsername);
                triggerSlam('home1');
            } else if (position === 2) {
                setTeam1Player2(playerUsername);
                triggerSlam('home2');
            }
        } else {
            if (position === 1) {
                setTeam2Player1(playerUsername);
                triggerSlam('away1');
            } else if (position === 2) {
                setTeam2Player2(playerUsername);
                triggerSlam('away2');
            }
        }

        setOtherPlayers([]);
        setSelectedPlayer(null);
        setSearchQuery('');
        setErrorMessage(null);
    };

    const isStartDisabled = () => {
        return !team1Player1 || !team1Player2 || !team2Player1 || !team2Player2;
    };

    const handleStartGame = async () => {
        const team1PlayersSet = new Set([team1Player1, team1Player2]);
        const team2PlayersSet = new Set([team2Player1, team2Player2]);

        if (
            team1PlayersSet.size === 2 &&
            team2PlayersSet.size === 2 &&
            ![...team1PlayersSet].some(player => team2PlayersSet.has(player))
        ) {
            setFlashing(true);
            setTimeout(() => setFlashing(false), 350);

            // Creator (team1Player1 = username from auth) is identified by JWT.
            // Send the other 3 players.
            const payload = {
                home2: team1Player2,
                away1: team2Player1,
                away2: team2Player2,
            };

            try {
                const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/g/lobby/create/`, payload, authHeader());
                const { gameID, lobbyID } = response.data;

                dispatch(setGameID(gameID));
                dispatch(setPlayers({
                    team1Players: [team1Player1, team1Player2],
                    team2Players: [team2Player1, team2Player2],
                }));

                navigate(`/lobby/${lobbyID}`);
            } catch (err) {
                console.error('Error creating lobby:', err);
                setErrorMessage('Failed to create the lobby.');
                setFlashing(false);
            }
        } else {
            setErrorMessage('Please select four unique players, two for each team.');
        }
    };

    const handleClearSelection = () => {
        setTeam1Player1(username);
        setTeam1Player2('');
        setTeam2Player1('');
        setTeam2Player2('');
        setSearchQuery('');
        setOtherPlayers([]);
        setSelectedPlayer(null);
        setErrorMessage(null);
        setSearchedPlayers([]);
    };

    const getSlotClass = (side, filled, slotKey) => {
        const classes = [styles.playerSlot];
        if (filled) {
            classes.push(slotKey === slamSlot ? styles.slamIn : styles.playerSlotFilled);
        }
        return classes.join(' ');
    };

    return (
        <div className={styles.gameWrapper}>
            {flashing && <div className={styles.flashOverlay} />}

            <button className={styles.backBtn} onClick={() => navigate(-1)}>← Back</button>

            {/* VS split layout */}
            <div className={styles.versusLayout}>
                {/* Home Side */}
                <div className={styles.homeSide}>
                    <div className={styles.teamLabel}>HOME</div>
                    <div className={`${getSlotClass('home', !!team1Player1, 'home1')} ${styles.playerSlotHost}`}>
                        {team1Player1 || 'PLAYER 1'}
                        {team1Player1 && <span className={styles.youBadge}>YOU</span>}
                    </div>
                    <div className={getSlotClass('home', !!team1Player2, 'home2')}>
                        {team1Player2 || 'PLAYER 2'}
                    </div>
                </div>

                {/* VS Badge */}
                <div className={styles.vsBadge}>VS</div>

                {/* Away Side */}
                <div className={styles.awaySide}>
                    <div className={styles.teamLabel}>AWAY</div>
                    <div className={getSlotClass('away', !!team2Player1, 'away1')}>
                        {team2Player1 || 'PLAYER 1'}
                    </div>
                    <div className={getSlotClass('away', !!team2Player2, 'away2')}>
                        {team2Player2 || 'PLAYER 2'}
                    </div>
                </div>
            </div>

            {/* Search / Assign Section */}
            <div className={styles.searchSection}>
                <div className={styles.searchRow}>
                    <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search for other players"
                        className={styles.searchInput}
                        onKeyDown={(e) => e.key === 'Enter' && handleSearchUsername()}
                    />
                    <button className={styles.searchBtn} onClick={handleSearchUsername}>
                        Search
                    </button>
                </div>

                {errorMessage && (
                    <p className={styles.errorMessage}>{errorMessage}</p>
                )}

                {otherPlayers.length > 0 && !selectedPlayer && (
                    <div className={styles.dropdown}>
                        {otherPlayers.slice(0, 5).map((player, index) => (
                            <div
                                key={index}
                                className={styles.dropdownItem}
                                onClick={() => handleSelectPlayer(player)}
                            >
                                {player.username}
                            </div>
                        ))}
                    </div>
                )}

                {selectedPlayer && (
                    <>
                        <div className={styles.selectedPlayerRow}>
                            <span className={styles.selectedPlayerName}>{selectedPlayer.username}</span>
                            <button className={styles.deselectBtn} onClick={() => setSelectedPlayer(null)}>✕ Deselect</button>
                        </div>
                        <div className={styles.assignButtons}>
                            {!team1Player2 && (
                                <button
                                    className={styles.homeBtn}
                                    onClick={() => handleAssignOtherPlayer(selectedPlayer, 'home', 2)}
                                >
                                    Assign to Home
                                </button>
                            )}
                            {(!team2Player1 || !team2Player2) && (
                                <button
                                    className={styles.awayBtn}
                                    onClick={() => handleAssignOtherPlayer(selectedPlayer, 'away', team2Player1 ? 2 : 1)}
                                >
                                    Assign to Away
                                </button>
                            )}
                        </div>
                    </>
                )}

                <div style={{ textAlign: 'center' }}>
                    <button className={styles.clearBtn} onClick={handleClearSelection}>
                        Clear All
                    </button>
                </div>
            </div>

            {/* Start Game Button */}
            <div className={styles.startBtnWrapper}>
                <button
                    className={`${styles.startBtn} ${isStartDisabled() ? styles.startBtnDisabled : styles.startBtnReady}`}
                    onClick={handleStartGame}
                    disabled={isStartDisabled()}
                >
                    START GAME
                </button>
            </div>
        </div>
    );
};

export default StartGame;
